import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AnnualPlan} from '../../../common/models/annual-plan';
import {AnnualPlanMacroCycle} from '../../../common/models/annual-plan-macro-cycle';
import {AnnualPlanPhase} from '../../../common/models/annual-plan.phase';
import {Athlete} from '../../../common/models/athlete';
import {Organization} from '../../../common/models/organization';
import {Week} from '../../../common/models/week';
import { AnnualPlanService } from '../../providers/annual-plans.services';
import { AnnualPlanEffectiveData, EffectiveWeek } from '../../merger/annual-plan-data-merger';
import { EffectivePlanGraphDialogComponent } from './effective-plan-graph-dialog.component';
import { HorizontalScrollDirective } from '../../directive/horizontal-scroll';
import BaseGraph from './base-graph';
import { Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, KeyValuePipe, NgClass, NgForOf, NgIf, NgStyle } from "@angular/common";
import { MatRipple, MatRippleModule } from "@angular/material/core";
import { MatProgressSpinner, MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { AppDatePipe } from "../../../common/pipes/app-date";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MacroCycle } from "../../../common/models/macro-cycle";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";

@Component({
  selector: 'app-effective-plan-graph',
  templateUrl: './effective-plan-graph.component.html',
  styleUrls: ['./graph.scss', './effective-graph.scss'],
  imports: [
    FlexLayoutModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    TranslateModule,
    HorizontalScrollDirective,
    AppDatePipe,
    NgIf,
    NgForOf,
    KeyValuePipe,
    MatRippleModule,
  ],
  standalone: true
})
export class EffectivePlanGraphComponent extends BaseGraph implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public organization: Organization = new Organization({});
  @Input() public athlete: Athlete = new Athlete({});
  @Input() public annualPlanId: number | undefined;
  @Input() public scrollPosition: number | undefined;
  @Input() public override annualPlan: AnnualPlan = new AnnualPlan({});
  @Input() public override weeks: EffectiveWeek[] = [];
  @Input() public annualPlanPhases: AnnualPlanPhaseData[] = [];
  @Input() public annualPlanMacroCycles: AnnualPlanMacroCycleData[] = [];
  @Input() public override clickedWeek: Week = new Week({});
  @Input() public override viewMode = 'big';
  @Input() public override showMonths = false;

  @Output() public weeksChange = new EventEmitter<Week[]>();
  @Output() public override graphScrollPosition = new EventEmitter();

  @ViewChild('scrollable', {static: false})
  public override graphInnerDiv!: ElementRef;

  @ViewChild(HorizontalScrollDirective, {static: false})
  public override scroll!: HorizontalScrollDirective;

  public currentLang: string;

  private subscriptions: Subscription[] = [];

  constructor(
    public dialog: MatDialog,
    private annualPlanProvider: AnnualPlanService,
    private ts: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
    this.currentLang = ts.currentLang;
  }

  public ngAfterViewInit() {
    this.loadEffectivePlanData();
  }

  public ngOnChanges(changes: SimpleChanges) {
    this.onChanges(changes);
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public loadEffectivePlanData() {
    this.subscriptions.push(
      this.annualPlanProvider.findEffectiveByPlanId(this.organization!.code!, this.athlete.id!, this.annualPlanId!).subscribe((data: any) => {
        this.annualPlan = data['plan'] as AnnualPlan;
        this.weeks = data['weeks'];
        this.annualPlanPhases = data['phases'];
        this.annualPlanMacroCycles = data['macroCycles'];
        this.loadGraph();
        this.isLoading = false;
        this.cdr.detectChanges();
      })
    );
  }

  public onWeekClicked(week: any) {
    this.clickedSelector = week.id;

    const dialogRef = this.dialog.open(EffectivePlanGraphDialogComponent, {
      data: {week}
    });

    this.subscriptions.push(
      dialogRef.afterClosed().subscribe((result) => {
        this.clickedSelector = null;
        this.cdr.detectChanges();
      })
    );
  }

  public hasData() {
    for (const week of this.weeks) {
      if (week.intensityPercent > 0) {
        return true;
      }
    }
    return false;
  }

}


interface AnnualPlanMacroCycleData {
  annualPlanMacroCycleId: number,
  annualPlanMacroCycleWeekFrom: number,
  annualPlanMacroCycleWeekTo: number,
  macroCycleId: number,
  macroCycleHexadecimalColor: string,
  macroCycleTranslationId: number,
  macroCycleTranslationMacroCycleId: number,
  macroCycleTranslationName: string,
}

interface AnnualPlanPhaseData {
  annualPlanPhaseId: number,
  annualPlanPhaseWeekFrom: number,
  annualPlanPhaseWeekTo: number,
  phaseId: number,
  phaseHexadecimalColor: string,
  phaseTranslationId: number,
  phaseTranslationPhaseId: number,
  phaseTranslationName: string,
}
